// src/components/sessions/modals/AddSessionModal.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';

const AddSessionModal = ({
  isOpen,
  onClose,
  addSessionError,
  newSessionTitle,
  setNewSessionTitle,
  selectedDate,
  setSelectedDate,
  selectedHour,
  setSelectedHour,
  selectedMinute,
  setSelectedMinute,
  newSessionDuration,
  setNewSessionDuration,
  newSessionTutorIMId,
  setNewSessionTutorIMId,
  recordSession,
  setRecordSession,
  calendarMonth,
  setCalendarMonth,
  showHourDropdown,
  setShowHourDropdown,
  showMinuteDropdown,
  setShowMinuteDropdown,
  hourDropdownRef,
  minuteDropdownRef,
  courseTeachers,
  user,
  isAddingSession,
  onAddSession,
  getDaysInMonth,
  isToday,
  isPastDate,
  getAvailableHours,
  getAvailableMinutes,
  durationOptions
}) => {
  const { t, i18n } = useTranslation();

  if (!isOpen) return null;

  const weekdayLabels = [
    t('calendar.weekdays.monShort', 'M'),
    t('calendar.weekdays.tueShort', 'T'),
    t('calendar.weekdays.wedShort', 'W'),
    t('calendar.weekdays.thuShort', 'T'),
    t('calendar.weekdays.friShort', 'F'),
    t('calendar.weekdays.satShort', 'S'),
    t('calendar.weekdays.sunShort', 'S')
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {t('sessions.modals.addSession.title')}
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label={t('common.close')}
            >
              <XCircleIcon className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
        
        <div className="p-5">
          {addSessionError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {addSessionError}
            </div>
          )}

          <div className="space-y-4">
            {/* Session Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('sessions.modals.addSession.sessionTitle')}
              </label>
              <input
                type="text"
                value={newSessionTitle}
                onChange={(e) => setNewSessionTitle(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder={t('sessions.modals.addSession.titlePlaceholder')}
              />
            </div>

            {/* Date and Time Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('sessions.modals.addSession.date')}
                </label>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
                      className="p-1 hover:bg-white rounded transition-colors"
                      aria-label={t('calendar.previousMonth')}
                    >
                      <ChevronLeftIcon className="w-3.5 h-3.5 text-slate-600" />
                    </button>
                    <span className="text-sm font-medium text-slate-800">
                      {calendarMonth.toLocaleDateString(i18n.language, { month: 'short', year: 'numeric' })}
                    </span>
                    <button
                      onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
                      className="p-1 hover:bg-white rounded transition-colors"
                      aria-label={t('calendar.nextMonth')}
                    >
                      <ChevronRightIcon className="w-3.5 h-3.5 text-slate-600" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-xs">
                    {weekdayLabels.map((day, idx) => (
                      <div key={idx} className="text-center text-xs font-medium text-slate-500 py-1">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-xs">
                    {getDaysInMonth(calendarMonth).map((date, index) => (
                      <button
                        key={index}
                        onClick={() => date && !isPastDate(date) && setSelectedDate(date)}
                        disabled={!date || isPastDate(date)}
                        className={`
                          p-1.5 rounded text-xs transition-all
                          ${!date ? 'invisible' : ''}
                          ${date && isPastDate(date) ? 'text-slate-300 cursor-not-allowed' : ''}
                          ${date && !isPastDate(date) && !isToday(date) && selectedDate?.toDateString() !== date.toDateString() ? 'hover:bg-white text-slate-700' : ''}
                          ${date && isToday(date) && selectedDate?.toDateString() !== date.toDateString() ? 'bg-cyan-50 text-cyan-600 font-semibold' : ''}
                          ${date && selectedDate?.toDateString() === date.toDateString() ? 'bg-cyan-600 text-white font-semibold' : ''}
                        `}
                      >
                        {date?.getDate()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Time Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('sessions.modals.addSession.time')}
                </label>
                <div className="flex gap-2">
                  {/* Hour Dropdown */}
                  <div className="relative flex-1 dropdown-container">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowHourDropdown(!showHourDropdown);
                        setShowMinuteDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white hover:bg-slate-50 transition-colors flex items-center justify-between"
                    >
                      <span className="font-medium">
                        {selectedHour !== null ? selectedHour.toString().padStart(2, '0') : t('sessions.modals.addSession.hourPlaceholder')}
                      </span>
                      <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    
                    {showHourDropdown && (
                      <div ref={hourDropdownRef} className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {getAvailableHours().map((hour, idx) => (
                          <button
                            key={hour}
                            type="button"
                            data-value={hour}
                            data-index={idx}
                            onClick={() => {
                              setSelectedHour(hour);
                              setShowHourDropdown(false);
                              const availableMinutes = getAvailableMinutes();
                              if (selectedMinute !== null && !availableMinutes.includes(selectedMinute)) {
                                setSelectedMinute(availableMinutes[0] || 0);
                              }
                            }}
                            className={`w-full px-3 py-1.5 text-left hover:bg-cyan-50 transition-colors text-sm ${
                              selectedHour === hour ? 'bg-cyan-50 text-cyan-700 font-medium' : ''
                            }`}
                          >
                            {hour.toString().padStart(2, '0')}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <span className="flex items-center text-slate-400 font-medium text-sm">:</span>

                  {/* Minute Dropdown */}
                  <div className="relative flex-1 dropdown-container">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMinuteDropdown(!showMinuteDropdown);
                        setShowHourDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white hover:bg-slate-50 transition-colors flex items-center justify-between"
                    >
                      <span className="font-medium">
                        {selectedMinute !== null ? selectedMinute.toString().padStart(2, '0') : t('sessions.modals.addSession.minutePlaceholder')}
                      </span>
                      <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    
                    {showMinuteDropdown && (
                      <div ref={minuteDropdownRef} className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {getAvailableMinutes().map((minute, idx) => (
                          <button
                            key={minute}
                            type="button"
                            data-value={minute}
                            data-index={idx}
                            onClick={() => {
                              setSelectedMinute(minute);
                              setShowMinuteDropdown(false);
                            }}
                            className={`w-full px-3 py-1.5 text-left hover:bg-cyan-50 transition-colors text-sm ${
                              selectedMinute === minute ? 'bg-cyan-50 text-cyan-700 font-medium' : ''
                            }`}
                          >
                            {minute.toString().padStart(2, '0')}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Duration Pills */}
                <div className="mt-3">
                  <label className="block text-xs font-medium text-slate-600 mb-2">
                    {t('sessions.modals.addSession.duration')}
                  </label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {durationOptions.slice(0, 6).map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setNewSessionDuration(option.value)}
                        className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          newSessionDuration === option.value
                            ? 'bg-cyan-600 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Teacher Selection (if multiple) */}
            {courseTeachers.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('sessions.modals.addSession.teacher')}
                </label>
                <select
                  value={newSessionTutorIMId}
                  onChange={(e) => setNewSessionTutorIMId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none bg-white"
                >
                  {courseTeachers.map(teacher => (
                    <option key={teacher.institution_member_id} value={teacher.institution_member_id}>
                      {teacher.display_name || teacher.full_name || teacher.email}
                      {(teacher.user_id === user?.id || teacher.user_id === user?.userId) ? ` (${t('common.you')})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Recording Option */}
            <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="recordSession"
                checked={recordSession}
                onChange={(e) => setRecordSession(e.target.checked)}
                className="w-4 h-4 text-cyan-600 border-slate-300 rounded focus:ring-cyan-500"
              />
              <label htmlFor="recordSession" className="flex items-center cursor-pointer flex-1">
                <VideoCameraIcon className="w-4 h-4 mr-2 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">
                  {t('sessions.modals.addSession.recordSession')}
                </span>
              </label>
            </div>

            <p className="text-xs text-slate-500 -mt-2 px-1">
              {t('sessions.modals.addSession.recordingHint')}
            </p>

          </div>

          {/* Action Buttons */}
          <div className="mt-5 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={onAddSession}
              disabled={isAddingSession || !selectedDate || selectedHour === null || selectedMinute === null}
              className="flex-1 px-4 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed font-medium"
            >
              {isAddingSession ? t('sessions.modals.addSession.creating') : t('sessions.modals.addSession.createButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSessionModal;